import Scene, { worldFor } from "./observatory/Scene";
import { SectionCompass } from "./JourneyUX";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { track } from "@vercel/analytics";
import { Service, ritualGroups, whatsapp, servicePath } from "./catalog";
import { Arrow, Breadcrumb, DiscountNote } from "./Shared";
import { formatPrice, getPriceView } from "../utils/pricing";
import usePricing from "./PricingContext";
import Reviews from "./Reviews";
import { practiceGuides } from "./practiceContent";
import { additionalHealingServices } from "../pages/healingServices";

export function paymentPath(
  service: string,
  plan: string,
  amount: number,
  format?: string,
) {
  const query = new URLSearchParams({ service, plan, amount: String(amount) });
  if (format) query.set("callType", format);
  return `/payment?${query.toString()}`;
}
function BookingOptions({ service }: { service: Service }) {
  const { discountActive, discountMultiplier, loading } = usePricing();
  const [params,setParams] = useSearchParams();
  const group = params.get("group") === "By time" ? "By time" : "By question";
  const format = params.get("format") === "Voice Note" ? "Voice Note" : "Audio";
  const option = params.get("option");
  const index = option !== null && /^\d+$/.test(option) ? Number(option) : -1;
  const selected = index >= 0 && index < service.plans.length && (service.slug !== "tarot" || service.plans[index].group === group) ? index : null;
  const updateSelection = (values: Record<string,string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([key,value])=>value===null?next.delete(key):next.set(key,value));
    setParams(next,{replace:true,preventScrollReset:true});
  };
  const tarot = service.slug === "tarot";
  const plans = service.plans
    .map((plan, index) => ({ ...plan, index }))
    .filter((p) => !tarot || p.group === group);
  const selectedPlan = selected === null ? undefined : service.plans[selected];
  const selectedPrice =
    selectedPlan &&
    getPriceView(
      String(selectedPlan.price),
      discountActive,
      discountMultiplier,
    );
  return (
    <section id="plans" className="booking-section">
      <div className="container booking-grid">
        <div className="booking-intro">
          <p className="eyebrow">
            {service.category === "Courses"
              ? "CHOOSE HOW YOU LEARN"
              : "MAKE TIME FOR YOURSELF"}
          </p>
          <h2>
            {service.category === "Courses" ? (
              "Your next chapter."
            ) : (
              <>
                Find your <br />
                session.
              </>
            )}
          </h2>
          <p>
            Choose the option that feels right for you. Review your details
            before continuing to payment.
          </p>
          <a
            className="text-link"
            href={whatsapp(
              `Hello, I would like to check availability for ${service.title}.`,
            )}
            target="_blank"
            rel="noreferrer"
          >
            Check availability first <Arrow diagonal />
          </a>
          {service.note && <p className="booking-note">{service.note}</p>}
          {service.slug === "past-life" && (
            <a className="text-link" href="/docs/consent.docx" download>
              Download consent form ↓
            </a>
          )}
        </div>
        <div className="booking-options">
          <DiscountNote />
          {tarot && (
            <>
              <div
                className="filter-tabs booking-tabs"
                aria-label="Reading type"
              >
                {["By question", "By time"].map((value) => (
                  <button
                    key={value}
                    aria-pressed={group === value}
                    onClick={() => {
                      updateSelection({group:value,option:null});
                    }}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <fieldset className="format-choice">
                <legend>How would you like your reading?</legend>
                {["Audio", "Voice Note"].map((value) => (
                  <label key={value}>
                    <input
                      type="radio"
                      name="reading-format"
                      value={value}
                      checked={format === value}
                      onChange={() => updateSelection({format:value})}
                    />
                    {value === "Audio" ? "Audio call" : "Voice note"}
                  </label>
                ))}
              </fieldset>
            </>
          )}
          <fieldset className="plan-list">
            <legend className="sr-only">
              Choose a{" "}
              {service.category === "Courses" ? "course format" : "session"}
            </legend>
            {plans.map((plan) => {
              const price = getPriceView(
                String(plan.price),
                discountActive,
                discountMultiplier,
              );
              return (
                <label
                  className={`plan-option ${selected === plan.index ? "selected" : ""}`}
                  key={plan.index}
                >
                  <input
                    type="radio"
                    name="session-plan"
                    value={plan.index}
                    checked={selected === plan.index}
                    onChange={() => updateSelection({option:String(plan.index)})}
                  />
                  <span className="session-option-number" aria-hidden="true">{String(plan.index+1).padStart(2,"0")}</span>
                  <span className="plan-copy">
                    <strong>{plan.title}</strong>
                    <small>{plan.duration}</small>
                    {plan.note && <span>{plan.note}</span>}
                  </span>
                  <span className="plan-price">
                    {discountActive && <del>{price.originalText}</del>}
                    {loading ? "…" : price.finalText}
                  </span>
                </label>
              );
            })}
          </fieldset>
          <div className="booking-summary" aria-live="polite">
            {selectedPlan && selectedPrice ? (
              <>
                <div>
                  <span>✦ Your next chapter</span>
                  <strong>{selectedPlan.title}</strong>
                  <small>
                    {selectedPlan.duration}
                    {tarot
                      ? ` · ${format === "Audio" ? "Audio call" : "Voice note"}`
                      : ""}
                  </small>
                </div>
                <Link
                  className="button"
                  aria-disabled={loading}
                  state={{returnTo:`${servicePath(service)}?${params.toString()}#plans`}}
                  to={paymentPath(
                    service.paymentName || service.title,
                    `${selectedPlan.title} — ${selectedPlan.duration}`,
                    selectedPrice.amount,
                    tarot ? format : undefined,
                  )}
                  onClick={(e) => {
                    if (loading) e.preventDefault();
                    else
                      track("Booking selected", {
                        service: service.title,
                        plan: selectedPlan.title,
                      });
                  }}
                >
                  Continue · {formatPrice(selectedPrice.amount)} <Arrow />
                </Link>
              </>
            ) : (
              <p>Select an option above to continue.</p>
            )}
          </div>
          {service.category === "Courses" &&
            service.slug === "courses/tarot" && (
              <a
                className="text-link demo-link"
                href={whatsapp(
                  "Hello, I would like to request a demo session for the Tarot Mastery Course.",
                )}
                target="_blank"
                rel="noreferrer"
              >
                Try a demo session first <Arrow diagonal />
              </a>
            )}
          <p className="booking-footnote">
            Your time slot is arranged on WhatsApp. Payment alone does not
            confirm a session time.
          </p>
        </div>
      </div>
    </section>
  );
}
function RitualOptions() {
  const { discountActive, discountMultiplier, loading } = usePricing();
  const [group, setGroup] = useState(0);
  const [intention, setIntention] = useState("Attracting new love");
  const [duration, setDuration] = useState("1 day");
  const ritual = ritualGroups[group];
  const price = getPriceView(
    String(duration === "7 days" ? 2300 : ritual.price),
    discountActive,
    discountMultiplier,
  );
  return (
    <section id="plans" className="booking-section">
      <div className="container booking-grid">
        <div className="booking-intro">
          <p className="eyebrow">START WITH AN INTENTION</p>
          <h2>
            A ritual,
            <br />
            <em>chosen by you.</em>
          </h2>
          <p>
            Choose a focus, then your intention and duration. For a custom
            ritual, speak with Vaanii before payment.
          </p>
          <p className="booking-note">
            Rituals are spiritual practices. Individual experiences vary and
            specific outcomes are not guaranteed.
          </p>
        </div>
        <div className="ritual-form">
          <DiscountNote />
          <label>
            1. Choose your focus
            <select
              value={group}
              onChange={(e) => {
                const next = Number(e.target.value);
                setGroup(next);
                setIntention(ritualGroups[next].items[0]);
              }}
            >
              {ritualGroups.map((r, i) => (
                <option key={r.title} value={i}>
                  {r.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            2. Choose your intention
            <select
              value={intention}
              onChange={(e) => setIntention(e.target.value)}
            >
              {ritual.items.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </label>
          <fieldset className="duration-choice">
            <legend>3. Choose your duration</legend>
            {["1 day", "7 days", "Custom"].map((d) => (
              <label key={d}>
                <input
                  type="radio"
                  name="duration"
                  checked={duration === d}
                  onChange={() => setDuration(d)}
                />
                {d}
              </label>
            ))}
          </fieldset>
          <div className="ritual-total">
            <span>
              {duration === "Custom"
                ? "Personalised ritual"
                : `${duration} · ${intention}`}
            </span>
            <strong>
              {duration === "Custom"
                ? "Let’s discuss"
                : loading
                  ? "…"
                  : price.finalText}
            </strong>
            {discountActive && duration !== "Custom" && (
              <del>{price.originalText}</del>
            )}
          </div>
          {duration === "Custom" ? (
            <a
              className="button"
              href={whatsapp(
                `Hello, I would like to discuss a custom ${ritual.title} ritual for ${intention}.`,
              )}
              target="_blank"
              rel="noreferrer"
            >
              Discuss your ritual <Arrow diagonal />
            </a>
          ) : (
            <Link
              className="button"
              aria-disabled={loading}
              onClick={(e) => {
                if (loading) e.preventDefault();
              }}
              to={paymentPath(
                ritual.title,
                `${intention} — ${duration}`,
                price.amount,
              )}
            >
              Review booking <Arrow />
            </Link>
          )}
          <p className="booking-footnote">
            You’ll review your selection before payment.
          </p>
        </div>
      </div>
    </section>
  );
}
export default function ServiceDetail({ service }: { service: Service }) {
  const isCourse = service.category === "Courses";
  const guide = practiceGuides[service.slug];
  return (
    <div className={`service-world observatory-service world-${service.category.toLowerCase()}`} key={service.slug}>
      <div className="container">
        <Breadcrumb
          current={service.title}
          parent={isCourse ? "Courses" : "Services"}
          parentPath={isCourse ? "/services/courses" : "/services"}
        />
      </div>
      <section id="service-overview" className="practice-entrance container">
        <div className="practice-entrance-top"><p className="observatory-label">{worldFor(service.slug).symbol} / {service.category}</p><div><span>{worldFor(service.slug).name}</span>{!service.comingSoon&&<a href="#plans">View sessions ↘</a>}</div></div>
        <div className="practice-entrance-title"><h1>{service.title}</h1><p>{service.intro}</p></div>
        <div className="practice-entrance-scene"><Scene slug={service.slug} large/></div>
        <div className="practice-entrance-bottom"><div><p>{service.description}</p><span>{service.format}</span></div>{service.comingSoon?<Link className="portal-button" to="/services/mobile-number-correction"><span>Explore number analysis</span><Arrow/></Link>:<a className="portal-button" href="#plans"><span>{isCourse?'Begin a new chapter':'Find your session'}</span><Arrow/></a>}</div>
      </section>
      <SectionCompass items={[{id:"service-overview",label:"The practice"},...(!service.comingSoon?[{id:"plans",label:"Sessions & prices"}]:[]),...(guide?[{id:"practice-guide",label:"Explore deeper"}]:[]),...(service.curriculum?[{id:"curriculum",label:"Your curriculum"}]:[]),...(!service.comingSoon?[{id:"session-questions",label:"Good to know"}]:[])]}/>
      <div className="service-focus container">
        {service.focus.map((focus, index) => (
          <div key={focus}>
            <span>0{index + 1}</span>
            <p>{focus}</p>
          </div>
        ))}
      </div>
      {service.comingSoon ? (
        <section className="container coming-soon">
          <h2>
            A little more time,
            <br />
            <em>and a new offering.</em>
          </h2>
          <p>
            These numerology services are not open for booking yet. Get in touch
            to ask about availability, or explore the mobile number reports
            available now.
          </p>
          <a
            className="text-link"
            href={whatsapp(
              "Hello, I would like to ask when the numerology services will be available.",
            )}
            target="_blank"
            rel="noreferrer"
          >
            Ask about numerology <Arrow diagonal />
          </a>
        </section>
      ) : service.slug === "tarot-spells" ? (
        <RitualOptions />
      ) : (
        <BookingOptions service={service} />
      )}
      {guide && (
        <section id="practice-guide" className="container practice-detail-guide">
          <div>
            <p className="welcome-kicker">A little more about this practice</p>
            <h2>{guide.title}</h2>
            <p>{guide.introduction}</p>
          </div>
          <div className="practice-guide-items">
            {guide.items.map((item) => <article key={item.title}><span className="chapter-glyph" aria-hidden="true">✦</span><h3>{item.title}</h3><p>{item.text}</p></article>)}
          </div>
        </section>
      )}
      {service.slug === "maa-kamakhya-healing" && (
        <section className="container intentions-section">
          <div>
            <p className="eyebrow">PERSONALISE YOUR PRACTICE</p>
            <h2>
              What would you
              <br />
              <em>like to focus on?</em>
            </h2>
            <p>
              These intentions are part of Maa Kamakhya Healing. Discuss your
              focus with Vaanii before your session is finalised.
            </p>
          </div>
          <ul>
            {additionalHealingServices.map((item) => (
              <li key={item.slug}>
                <span>{item.title}</span>
                <a
                  href={whatsapp(
                    `Hello, I would like to discuss ${item.title} as a focus for Maa Kamakhya Healing.`,
                  )}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Discuss ${item.title}`}
                >
                  <Arrow diagonal />
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
      {service.curriculum && (
        <section id="curriculum" className="container curriculum-section">
          <div>
            <p className="eyebrow">WHAT YOU’LL EXPLORE</p>
            <h2>
              A practice built
              <br />
              <em>step by step.</em>
            </h2>
            <p>
              {service.slug === "courses/tarot"
                ? "Your 16-day tarot curriculum."
                : "Nine modules across two days. Modules 1–5 introduce oils; modules 6–9 cover sprays, rituals, and application."}
            </p>
          </div>
          <ol>
            {service.curriculum.map((lesson, i) => (
              <li key={lesson}>
                <span>{String(i + 1).padStart(2, "0")}</span>
                {lesson}
              </li>
            ))}
          </ol>
        </section>
      )}
      {!service.comingSoon && (
        <section id="session-questions" className="container service-expect">
          <div>
            <p className="eyebrow">
              BEFORE YOUR {isCourse ? "COURSE" : "SESSION"}
            </p>
            <h2>
              Come with questions.
              <br />
              We’ll take it from there.
            </h2>
          </div>
          <div>
            <details className="session-faq" open><summary>What happens after I book?</summary>
            <p>
              Share your payment confirmation on WhatsApp. We’ll arrange your
              time and explain any preparation. You can also ask about
              availability before making a payment.
            </p>
            </details><details className="session-faq"><summary>
              {service.slug === "relationship-healing"
                ? "Can I attend without my partner?"
                : "Can I talk to someone before choosing?"}
            </summary>
            <p>
              {service.slug === "relationship-healing"
                ? "Yes. Choose single person counselling to explore your own needs, triggers, and communication patterns. Couple sessions are for partners who both want to participate."
                : "Yes. If you’re unsure about the format or whether it is the right fit, start with a conversation with Vaanii."}
            </p>
            </details><a
              className="text-link"
              href={whatsapp(
                `Hello, I have a question about ${service.title}.`,
              )}
              target="_blank"
              rel="noreferrer"
            >
              Ask about {service.title} <Arrow diagonal />
            </a>
          </div>
        </section>
      )}
      {service.slug === "tarot" && <Reviews page="Tarot Reading" />}
      <div className="container back-to-services">
        <Link
          to={isCourse ? "/services/courses" : "/services"}
          className="text-link"
        >
          ← Explore other {isCourse ? "courses" : "services"}
        </Link>
        <a href="#main" className="text-link">
          Back to top ↑
        </a>
      </div>
    </div>
  );
}
